import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { AdminReportsService } from '../../admin-reports.service'
import {
  GetAdminCalendarInfo,
  GetCountReport,
  GetShopWithReports,
  AdminAddSalesReport,
  AdminGetSalesReport,
  AdminUpdateSalesReport,
  SetAdminCalendarFilters,
  AdminGetOneDaySalesReport
} from './admin-reports.actions'
import { AdminReportsStateModel } from './admin-reports.model'

@State<AdminReportsStateModel>({
  name: 'adminReports'
})
@Injectable()
export class AdminReportsState {
  constructor(private adminReportsService: AdminReportsService) {}

  @Selector()
  static calendarInfo(state: AdminReportsStateModel) {
    return state.calendarInfo
  }

  @Selector()
  static calendarFilters(state: AdminReportsStateModel) {
    return state.calendarFilters
  }

  @Selector()
  static countReportInfo(state: AdminReportsStateModel) {
    return state.countReportInfo
  }

  @Selector()
  static shopWithReports(state: AdminReportsStateModel) {
    return state.shopWithReports ?? []
  }

  @Selector()
  static salesReports(state: AdminReportsStateModel) {
    return state.salesReports ?? []
  }

  @Action(GetAdminCalendarInfo)
  getAdminCalendarInfo(ctx: StateContext<AdminReportsStateModel>, { payload }: GetAdminCalendarInfo) {
    return this.adminReportsService.calendar(payload).pipe(
      tap((res) => {
        ctx.patchState({ calendarInfo: res })
      })
    )
  }

  @Action(SetAdminCalendarFilters)
  setAdminCalendarFilters(
    ctx: StateContext<AdminReportsStateModel>,
    { payload }: SetAdminCalendarFilters
  ) {
    ctx.patchState({ calendarFilters: payload })
  }

  @Action(GetCountReport)
  getCountReport(ctx: StateContext<AdminReportsStateModel>, { excludeBatch }: GetCountReport) {
    return this.adminReportsService.getCountReport(excludeBatch).pipe(
      tap((res) => {
        ctx.patchState({ countReportInfo: res })
      })
    )
  }

  @Action(GetShopWithReports)
  getShopWithReports(ctx: StateContext<AdminReportsStateModel>, { payload }: GetShopWithReports) {
    return this.adminReportsService.getShopWithReports(payload).pipe(
      tap((res) => {
        ctx.patchState({ shopWithReports: res?.shopReportsItems ?? [] })
      })
    )
  }

  @Action(AdminGetSalesReport, { cancelUncompleted: true })
  getSalesReport(ctx: StateContext<AdminReportsStateModel>, { payload }: AdminGetSalesReport) {
    return this.adminReportsService.getSalesReport(payload).pipe(
      tap((res) => {
        ctx.patchState({ salesReports: res?.reports ?? [] })
      })
    )
  }

  @Action(AdminGetOneDaySalesReport, { cancelUncompleted: true })
  getOneDaySalesReport(
    ctx: StateContext<AdminReportsStateModel>,
    { payload }: AdminGetOneDaySalesReport
  ) {
    return this.adminReportsService.getSalesReport(payload).pipe(
      tap((res) => {
        const salesReports = ctx.getState()?.salesReports ?? []
        const foundIndex = salesReports.findIndex(
          (el) => el.day === payload.fromDate || `${el.day}`?.startsWith(`${payload.fromDate}`)
        )

        if (foundIndex > -1) {
          ctx.patchState({
            salesReports: salesReports?.map((el, index) =>
              index === foundIndex ? res?.reports?.[0] : el
            )
          })
        } else {
          ctx.patchState({
            salesReports: [...salesReports, ...res.reports]
          })
        }
      })
    )
  }

  @Action(AdminAddSalesReport)
  addSalesReport(ctx: StateContext<AdminReportsStateModel>, { payload }: AdminAddSalesReport) {
    return this.adminReportsService.addSalesReport(payload)
  }

  @Action(AdminUpdateSalesReport)
  updateSalesReport(ctx: StateContext<AdminReportsStateModel>, { payload }: AdminUpdateSalesReport) {
    return this.adminReportsService.updateSalesReport(payload)
  }
}
